import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Spinner, Alert } from "reactstrap";
import axios from "axios";
import DateRangeSelector from "../components/BI/DateRangeSelector";
import KPICards from "../components/BI/KPICards";
import RevenueVsCostChart from "../components/BI/RevenueVsCostChart";
import ValueStreamPerformanceChart from "../components/BI/ValueStreamPerformanceChart";
import ProjectsTimelineChart from "../components/BI/ProjectsTimelineChart";
import InstructorUtilizationChart from "../components/BI/InstructorUtilizationChart";
import ProjectsListTable from "../components/BI/ProjectsListTable";
import { API_URL_BI_SUMMARY, API_URL_BI_VALUE_STREAM, API_URL_BI_TIMELINE, API_URL_BI_PROJECTS } from "../constants";
import { getAuthHeaders } from "../utils/auth";

// 🔧 Debug flag - set to false to disable logs
const BI_DEBUG = true;

const log = (...args) => BI_DEBUG && console.log(...args);
const logError = (...args) => console.error(...args); // Always log errors

/**
 * Transform flat projects array into grouped structure for ProjectsListTable
 */
const transformProjectsForTable = (projectsArray) => {
  if (!Array.isArray(projectsArray) || projectsArray.length === 0) {
    return [];
  }

  const grouped = {};

  projectsArray.forEach(project => {
    const valueStream = project.sede?.name || 'Unassigned';

    if (!grouped[valueStream]) {
      grouped[valueStream] = {
        value_stream: valueStream,
        projects: [],
        totals: {
          count: 0,
          revenue: 0,
          cost: 0,
          profit: 0,
          margin: 0
        }
      };
    }

    const transformedProject = {
      proyecto_code: project.proyecto_code,
      proyecto_name: project.name,
      registration_date: project.registration_date, // ⭐ CRITICAL: Pass through registration_date
      status: project.status,
      revenue: project.financials?.total_revenue || 0,
      cost: project.financials?.total_expenses || 0,
      profit: project.financials?.net_profit || 0,
      margin: project.financials?.profit_margin || 0
    };

    grouped[valueStream].projects.push(transformedProject);

    // Update totals
    grouped[valueStream].totals.count += 1;
    grouped[valueStream].totals.revenue += transformedProject.revenue;
    grouped[valueStream].totals.cost += transformedProject.cost;
    grouped[valueStream].totals.profit += transformedProject.profit;
  });

  // Calculate average margin
  Object.values(grouped).forEach(stream => {
    if (stream.totals.revenue > 0) {
      stream.totals.margin = ((stream.totals.profit / stream.totals.revenue) * 100).toFixed(2);
    } else {
      stream.totals.margin = 0;
    }
  });

  return Object.values(grouped).sort((a, b) => b.totals.revenue - a.totals.revenue);
};

/**
 * Get default date range (last 30 days)
 */
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  return {
    startDate,
    endDate,
    preset: 'last_30_days',
    granularity: 'day' // Default to daily for 30 days
  };
};

const BusinessIntelligence = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    summary: null,
    valueStream: null,
    timeline: null,
    projects: null
  });

  useEffect(() => {
    fetchBIData();
  }, [dateRange]);

  const fetchBIData = async () => {
    setLoading(true);
    setError(null);

    const headers = getAuthHeaders();
    const params = {
      start_date: formatDate(dateRange.startDate),
      end_date: formatDate(dateRange.endDate)
      // Note: granularity not sent to backend (not supported yet)
    };

    log("📊 BI Query:", {
      range: `${params.start_date} → ${params.end_date}`,
      days: Math.floor((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24))
    });

    try {
      const [summaryRes, valueStreamRes, timelineRes, projectsRes] = await Promise.allSettled([
        axios.get(API_URL_BI_SUMMARY, { headers, params }),
        axios.get(API_URL_BI_VALUE_STREAM, { headers, params }),
        axios.get(API_URL_BI_TIMELINE, { headers, params }),
        axios.get(API_URL_BI_PROJECTS, { headers, params })
      ]);

      // Extract successful responses
      const summary = summaryRes.status === 'fulfilled' ? summaryRes.value : null;
      const valueStream = valueStreamRes.status === 'fulfilled' ? valueStreamRes.value : null;
      const timeline = timelineRes.status === 'fulfilled' ? timelineRes.value : null;
      const projects = projectsRes.status === 'fulfilled' ? projectsRes.value : null;

      // Log failures
      if (summaryRes.status === 'rejected') logError("❌ Summary API failed:", summaryRes.reason.message);
      if (valueStreamRes.status === 'rejected') logError("❌ Value Stream API failed:", valueStreamRes.reason.message);
      if (timelineRes.status === 'rejected') logError("❌ Timeline API failed:", timelineRes.reason.message);
      if (projectsRes.status === 'rejected') logError("❌ Projects API failed:", projectsRes.reason.message);

      // 🔍 DEBUG: Check if registration_date is coming from API
      if (projects?.data?.projects?.[0]) {
        log("🔍 Sample project from API:", {
          codigo: projects.data.projects[0].proyecto_code,
          name: projects.data.projects[0].name,
          has_registration_date: !!projects.data.projects[0].registration_date,
          registration_date: projects.data.projects[0].registration_date,
          inicio_date: projects.data.projects[0].inicio_date
        });
      } else {
        log("⚠️ No projects returned from API");
      }

      // 🔍 DEBUG: Check counts
      if (summary?.data) {
        log("📊 Summary Counts:", {
          active_projects: summary.data.counts?.active_projects,
          total_projects: summary.data.counts?.total_projects,
          revenue: `$${summary.data.summary?.total_revenue?.toLocaleString() || 0}`,
          profit: `$${summary.data.summary?.net_profit?.toLocaleString() || 0}`
        });
      }

      // Transform projects data
      const transformedProjects = transformProjectsForTable(projects?.data?.projects || []);
      
      // 🔍 DEBUG: Check transformed data
      if (transformedProjects?.[0]?.projects?.[0]) {
        log("🔍 Transformed project sample:", {
          stream: transformedProjects[0].value_stream,
          codigo: transformedProjects[0].projects[0].proyecto_code,
          has_registration_date: !!transformedProjects[0].projects[0].registration_date,
          registration_date: transformedProjects[0].projects[0].registration_date
        });
      } else {
        log("⚠️ No projects after transformation");
      }

      setData({
        summary: summary?.data || { summary: null, counts: null },
        valueStream: valueStream?.data || { value_streams: [] },
        timeline: timeline?.data || { timeline: [], project_milestones: [] },
        projects: transformedProjects
      });

      log("✅ BI data loaded successfully");
      
    } catch (err) {
      logError("❌ BI API Error:", err.message);
      if (err.response) {
        logError("Response:", err.response.status, err.response.data);
      }
      setError("Error al cargar los datos. Por favor intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handleDateRangeChange = (newRange) => {
    log("🔄 Date range changed:", {
      from: `${formatDate(dateRange.startDate)} → ${formatDate(dateRange.endDate)}`,
      to: `${formatDate(newRange.startDate)} → ${formatDate(newRange.endDate)}`,
      preset: newRange.preset,
      granularity: newRange.granularity
    });
    setDateRange(newRange);
  };

  return (
    <Container fluid className="business-intelligence-page">
      <Row className="mb-4">
        <Col>
          <h1 className="page-title">Business Intelligence</h1>
          <p className="text-muted">
            Métricas clave de negocio y análisis de rendimiento
          </p>
          {BI_DEBUG && (
            <div style={{fontSize: '10px', color: '#666', marginTop: '10px', fontFamily: 'monospace'}}>
              🔧 Debug: {formatDate(dateRange.startDate)} → {formatDate(dateRange.endDate)} 
              {' | '}Loading: {loading ? 'Yes' : 'No'}
              {' | '}Projects: {data.summary?.counts?.total_projects || 0}
              {' | '}Revenue: ${data.summary?.summary?.total_revenue?.toLocaleString() || 0}
            </div>
          )}
        </Col>
      </Row>

      {/* Date Range Selector */}
      <Row className="mb-4">
        <Col>
          <DateRangeSelector
            dateRange={dateRange}
            onChange={handleDateRangeChange}
          />
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert color="danger">
              {error}
              <div className="mt-2">
                <small>Check browser console (F12) for detailed error logs.</small>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {loading ? (
        <Row className="mb-4">
          <Col className="text-center">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3">Cargando datos...</p>
          </Col>
        </Row>
      ) : (
        <>
          {/* KPI Cards */}
          {data.summary && (
            <Row className="mb-4">
              <Col>
                <KPICards data={data.summary} />
              </Col>
            </Row>
          )}

          {/* Revenue vs Cost Trend */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Revenue vs Cost Trend</h5>
                </CardHeader>
                <CardBody>
                  {data.timeline && <RevenueVsCostChart data={data.timeline} />}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Value Stream Performance */}
          <Row className="mb-4">
            <Col lg="6">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Sales & Profit by Value Stream</h5>
                </CardHeader>
                <CardBody>
                  {data.projects && data.projects.length > 0 && (
                    <ValueStreamPerformanceChart data={{ value_streams: data.projects }} />
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Projects Timeline */}
            <Col lg="6">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Projects by Status</h5>
                </CardHeader>
                <CardBody>
                  {data.timeline && <ProjectsTimelineChart data={data.timeline} />}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Instructor Utilization */}
          {data.summary?.top_instructors && (
            <Row className="mb-4">
              <Col lg="12">
                <Card>
                  <CardHeader>
                    <h5 className="mb-0">Top Instructors by Hours Sold</h5>
                  </CardHeader>
                  <CardBody>
                    <InstructorUtilizationChart data={data.summary.top_instructors} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Projects List */}
          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Projects by Value Stream</h5>
                  <p className="text-muted small mb-0">
                    Lista detallada de todos los proyectos en el rango de fechas seleccionado
                  </p>
                </CardHeader>
                <CardBody>
                  {data.projects && data.projects.length > 0 ? (
                    <ProjectsListTable data={data.projects} />
                  ) : (
                    <div className="text-center text-muted py-4">
                      No hay proyectos en el rango de fechas seleccionado
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default BusinessIntelligence;
